import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import App from './App';
import './index.css';

// --- Debugging / Safety Harness ---
// This ensures that if the app crashes before React mounts, we see the error on screen.
window.onerror = function (message, source, lineno, colno, error) {
  console.error("Global Error Caught:", message, error);
  const errorContainer = document.createElement('div');
  errorContainer.style.cssText = `
    position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
    background: #fff; z-index: 999999; padding: 2rem; overflow: auto;
    font-family: monospace; color: #b91c1c; border: 4px solid #ef4444;
  `;
  errorContainer.innerHTML = `
    <h1 style="font-size: 1.5rem; margin-bottom: 1rem;">Application Crash (Startup)</h1>
    <div style="background: #fee2e2; padding: 1rem; border-radius: 0.5rem;">
      <p style="font-weight: bold; margin-bottom: 0.5rem;">${message}</p>
      <p style="font-size: 0.875rem; margin-bottom: 0.5rem;">Source: ${source}:${lineno}:${colno}</p>
      <pre style="white-space: pre-wrap; font-size: 0.75rem; background: #fff; padding: 0.5rem; border: 1px solid #fca5a5;">${error?.stack || 'No stack trace available'}</pre>
    </div>
    <button onclick="location.reload()" style="margin-top: 1rem; padding: 0.5rem 1rem; background: #333; color: white; border: none; border-radius: 0.25rem; cursor: pointer;">Reload Application</button>
  `;
  document.body.appendChild(errorContainer);
  
  // Remove spinner if it exists so it doesn't block the error
  const spinner = document.getElementById('loading-spinner');
  if (spinner) spinner.style.display = 'none';
};

// Catch Promise rejections (like dynamic import failures)
window.addEventListener('unhandledrejection', (event) => {
  console.error("Unhandled Promise Rejection:", event.reason);
});

console.log("Starting Application...");
console.log("React Version:", React.version);
// ----------------------------------

// Error Boundary to catch crashes and prevent white screen
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("React Error Boundary Caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', fontFamily: 'sans-serif', color: '#333', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
          <div style={{ maxWidth: '600px', width: '100%' }}>
            <h1 style={{ color: '#e11d48', marginBottom: '1rem' }}>Something went wrong.</h1>
            <p style={{ marginBottom: '1rem' }}>The application encountered an error while rendering.</p>
            <pre style={{ background: '#f5f5f5', padding: '1rem', borderRadius: '4px', overflowX: 'auto', fontSize: '0.875rem', border: '1px solid #e5e5e5' }}>
              {this.state.error?.toString()}
            </pre>
            <button 
              onClick={() => window.location.reload()}
              style={{ marginTop: '1.5rem', padding: '0.75rem 1.5rem', background: '#1a1a1a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const rootElement = document.getElementById('root');

if (!rootElement) {
  const msg = "FATAL: Could not find root element with id 'root' in index.html";
  console.error(msg);
  throw new Error(msg); // This will be caught by window.onerror
}

try {
  const app = (
    <React.StrictMode>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </React.StrictMode>
  );
  // The production build ships prerendered HTML (scripts/prerender.mjs): hydrate it.
  // In `vite dev` the root is empty, so render from scratch.
  if (rootElement.hasChildNodes()) {
    hydrateRoot(rootElement, app);
  } else {
    createRoot(rootElement).render(app);
  }
  console.log("React Root Mounted Successfully");
} catch (e) {
  console.error("Error during React mount:", e);
  throw e; // Pass to window.onerror
}
